import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, Play, Pause } from "@phosphor-icons/react";
import { toast } from "sonner";

const FREQS = ["weekly", "monthly", "quarterly", "yearly"];
const GST_RATES = [0, 5, 12, 18, 28];
const STATES = ["AN","AP","AR","AS","BR","CH","CG","DN","DD","DL","GA","GJ","HR","HP","JK","JH","KA","KL","LD","MP","MH","MN","ML","MZ","NL","OR","PY","PB","RJ","SK","TN","TG","TR","UP","UK","WB"];

const emptyItem = { description: "", hsn: "", qty: 1, unit_price: 0, gst_rate: 18 };

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Recurring() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = {
    name: "", client_name: "", client_gstin: "", client_state: "MH",
    frequency: "monthly",
    start_date: new Date().toISOString().slice(0, 10), end_date: "",
    day_of_period: 1, due_days: 30,
    items: [{ ...emptyItem }], tax_rate: 18, notes: "", active: true,
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/recurring-invoices").then((r) => setRows(r.data));
   
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/recurring-invoices", {
        ...form,
        end_date: form.end_date || null,
        tax_rate: Number(form.tax_rate),
        day_of_period: Number(form.day_of_period),
        due_days: Number(form.due_days),
        items: form.items.map((i) => ({
          description: i.description, hsn: i.hsn || "",
          qty: Number(i.qty), unit_price: Number(i.unit_price),
          gst_rate: Number(i.gst_rate ?? form.tax_rate ?? 0),
        })),
      });
      toast.success("Recurring template created");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const runNow = async (id) => {
    try {
      await api.post(`/recurring-invoices/${id}/run-now`);
      toast.success("Invoice generated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const toggle = async (r) => {
    try {
      await api.put(`/recurring-invoices/${r.id}`, { ...r, active: !r.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this recurring template?")) return;
    try { await api.delete(`/recurring-invoices/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const setItem = (idx, patch) => setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));

  return (
    <div data-testid="recurring-page">
      <PageHeader
        title="Recurring Invoices"
        subtitle="AMC contracts and periodic billing. Runs automatically every 30 minutes."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="create-recurring-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
                <Plus size={16} className="mr-2" /> New Template
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[520px] sm:max-w-lg overflow-y-auto">
              <SheetHeader><SheetTitle className="font-display">New Recurring Template</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <Field label="Template name">
                  <Input data-testid="recurring-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="AMC - Realty Corp Q1" />
                </Field>
                <Field label="Client name">
                  <Input data-testid="recurring-client-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Client GSTIN"><Input value={form.client_gstin} onChange={(e) => setForm({ ...form, client_gstin: e.target.value.toUpperCase() })} /></Field>
                  <Field label="Client state">
                    <Select value={form.client_state} onValueChange={(v) => setForm({ ...form, client_state: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Frequency">
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Day of period"><Input type="number" min="1" max="28" value={form.day_of_period} onChange={(e) => setForm({ ...form, day_of_period: e.target.value })} /></Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
                  <Field label="End date (optional)"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
                </div>
                <Field label="Due days after issue"><Input type="number" value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} /></Field>

                <div>
                  <Label className="label-caps mb-2 block">Items</Label>
                  <div className="space-y-2">
                    {form.items.map((it, idx) => (
                      <div key={`ri-${idx}`} className="grid grid-cols-[1fr_70px_50px_70px_60px_24px] gap-2 items-center">
                        <Input placeholder="Description" value={it.description} onChange={(e) => setItem(idx, { description: e.target.value })} />
                        <Input placeholder="HSN" value={it.hsn} onChange={(e) => setItem(idx, { hsn: e.target.value })} />
                        <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(idx, { qty: e.target.value })} />
                        <Input type="number" placeholder="Price" value={it.unit_price} onChange={(e) => setItem(idx, { unit_price: e.target.value })} />
                        <Select value={String(it.gst_rate)} onValueChange={(v) => setItem(idx, { gst_rate: Number(v) })}>
                          <SelectTrigger className="h-9 px-2"><SelectValue /></SelectTrigger>
                          <SelectContent>{GST_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                        <button onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button>
                      </div>
                    ))}
                    <button onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })} className="text-xs text-[#EA580C] font-semibold">+ ADD LINE</button>
                  </div>
                </div>

                <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                <Button onClick={submit} data-testid="save-recurring-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Save Template</Button>
              </div>
            </SheetContent>
          </Sheet>
        }
      />

      <div className="p-8">
        {rows.length === 0 ? (
          <EmptyState title="No recurring templates" description="Create your first AMC or subscription template — invoices generate automatically on schedule." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="recurring-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Template</TableHead>
                  <TableHead className="label-caps">Client</TableHead>
                  <TableHead className="label-caps">Frequency</TableHead>
                  <TableHead className="label-caps">Next Run</TableHead>
                  <TableHead className="label-caps text-right">Generated</TableHead>
                  <TableHead className="label-caps">Status</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`recurring-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-slate-600">{r.client_name}</TableCell>
                    <TableCell><span className="text-xs uppercase tracking-wider text-slate-700">{r.frequency}</span></TableCell>
                    <TableCell className="text-slate-600 font-mono-num">{fmtDate(r.next_run_date)}</TableCell>
                    <TableCell className="text-right font-mono-num">{r.generated_count}</TableCell>
                    <TableCell><StatusBadge status={r.active ? "in_progress" : "on_hold"} /></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => runNow(r.id)} data-testid={`run-recurring-${r.id}`} title="Run now" className="text-emerald-600 hover:text-emerald-800 p-1"><Play size={14} weight="fill" /></button>
                        <button onClick={() => toggle(r)} title={r.active ? "Pause" : "Resume"} className="text-slate-500 hover:text-[#EA580C] p-1"><Pause size={14} /></button>
                        <button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
